import { z } from 'zod';

import { resolveUnitToBaseRate } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitToBaseRate';
import { maxIssueQtyInUnit } from '#/modules/imsMgmt/receipts/stockIssue/helper/issueQtyBounds';

function isNonNegativeInteger(value) {
  if (value === '' || value == null) {
    return false;
  }
  const n = Number(value);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 0;
}

const draftLineSchema = z.object({
  mtrId: z.string().optional().nullable(),
  mtrNo: z.string().optional().nullable(),
  mtrName: z.string().optional().nullable(),
  invStockId: z.string().optional().nullable(),
  pendingAllocationId: z.string().optional().nullable(),
  stockNo: z.string().optional().nullable(),
  /** 基本單位可用量（非所選 unit 語意） */
  availableQty: z.union([z.string(), z.number()]).optional().nullable(),
  /** 可選；空則後端用物料 base */
  unitId: z.string().optional().nullable(),
  unitOptions: z.array(z.any()).optional().nullable(),
  qty: z.union([z.string(), z.number(), z.null()]),
  fromWarehouseLabel: z.string().optional().nullable(),
  remark: z.string().optional().nullable(),
}).superRefine((line, ctx) => {
  if (!line.invStockId && !line.pendingAllocationId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '請選擇庫存',
      path: ['invStockId'],
    });
    return;
  }

  const qty = line.qty;
  if (!isNonNegativeInteger(qty)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '退料數量須為 ≥ 0 的整數',
      path: ['qty'],
    });
    return;
  }

  const n = Number(qty);
  if (line.pendingAllocationId && n <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '待分配來源的退料數量須大於 0',
      path: ['qty'],
    });
    return;
  }
  const available = Number(line.availableQty);
  if (!Number.isFinite(available) || available <= 0) {
    return;
  }

  const maxQty = maxIssueQtyInUnit(available, line.unitOptions, line.unitId);
  if (n > maxQty) {
    const rate = resolveUnitToBaseRate(line.unitOptions, line.unitId) ?? 1;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: rate === 1
        ? `退料數量不可超過可用量（基本單位 ${available}）`
        : `退料數量不可超過本單位上限（${maxQty}；基本單位可用 ${available}）`,
      path: ['qty'],
    });
  }
});

export const stockReturnOperateSchema = z.object({
  returnDate: z.string().min(1, '請填寫退料日'),
  sourceWarehouseId: z.string().min(1, '請選擇來源倉'),
  targetWarehouseId: z.string().min(1, '請選擇目標倉'),
  issueReason: z.string().optional().nullable(),
  remark: z.string().optional().nullable(),
  draftLines: z.array(draftLineSchema).min(1, '請至少加入一筆明細'),
}).superRefine((values, ctx) => {
  if (values.sourceWarehouseId
    && values.targetWarehouseId
    && values.sourceWarehouseId === values.targetWarehouseId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '來源倉與目標倉不可相同',
      path: ['targetWarehouseId'],
    });
  }
});

export const STOCK_RETURN_FIELD_LABELS = {
  returnDate: '退料日',
  sourceWarehouseId: '來源倉',
  targetWarehouseId: '目標倉',
  issueReason: '事由',
  remark: '備註',
  draftLines: '退料明細',
  'draftLines.invStockId': '來源庫存',
  'draftLines.unitId': '單位',
  'draftLines.qty': '退料數量',
};
